import React, { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'

import TextInput from '../../../../shared/Inputs/TextInput/TextInput'
import SelectInput from '../../../../shared/Inputs/SelectInput/SelectInput'
import FileInput from '../../../../shared/Inputs/FileInput/FileInput'
import { UPLOADS_ENDPOINT } from '../../../../server-parameters'
import {
  validateName,
  stringDigitsLength,
  validateString,
  smartValidateString,
} from '../../../../validate/validators'
import { Country } from '../../../../types/common-types'
import { State } from '../../../../state-actions-reducers'
import warningImage from '../../../../images/warning.svg'
import DatePicker from '../../../../shared/Inputs/DatePicker/DatePicker'
import { getCompany } from '../../../../hooks/useCompanyCustomHook'
import AddressInputs from '../../AddressInputs/AddressInputs'
import { getFiscalFullName } from '../../../../utils/TranslateFiscalKgz/getFiscalFullName'
import { getFiscalShortName } from '../../../../utils/TranslateFiscalKgz/getFiscalShortName'
import { getCertificateRegistrationNumber } from '../../../../utils/TranslateFiscalKgz/getCertificateRegistrationNumber'
import { getCertificateRegistrationDate } from '../../../../utils/TranslateFiscalKgz/getCertificateRegistrationDate'
import { getCertificateRegistrationImage } from '../../../../utils/TranslateFiscalKgz/getCertificateRegistrationImage'
import styles from '../../FiscalScreenOne.module.scss'
import { foundingDocumentOptions } from '../../../../content/foundingDocument'
import InnExist from '../../../../components/IdentityExist/InnExist'

interface Props {
  lang: Lang
  accountForm: any
  addressCountries: Country[]
  regionCityRegistered: any
  regionCityFactual: any
}

/* REACT COMPONENT */
function CompanyScreenOne({
  lang,
  accountForm,
  addressCountries,
  regionCityRegistered,
  regionCityFactual,
}: Props) {
  const { watch, setValue, getValues, formState: { errors } } = useFormContext<State>()
  const state = watch()
  const [focus, setFocus] = useState('')

  async function autoFill() {
    let response = await getCompany(state.F_INN),
      fullName = response.suggestions[0]?.data.name.full_with_opf,
      shortName = response.suggestions[0]?.data.name.short_with_opf,
      ogrn = response.suggestions[0]?.data.ogrn
    if (
      (fullName && !state.F_FullName) ||
      (fullName && state.F_FullName && !validateName(state.F_FullName, false))
    )
      setValue('F_FullName', fullName, { shouldDirty: true })
    if (
      (shortName && !state.F_ShortName) ||
      (shortName &&
        state.F_ShortName &&
        !validateName(state.F_ShortName, false))
    )
      setValue('F_ShortName', shortName, { shouldDirty: true })
    if (
      (ogrn && !state.F_OGRN) ||
      (ogrn && state.F_OGRN && !stringDigitsLength(state.F_OGRN, 13, false))
    )
      setValue('F_OGRN', ogrn, { shouldDirty: true })
  }
  useEffect(() => {
    if (state.F_INN && stringDigitsLength(state.F_INN, 10, false)) autoFill()
  }, [state.F_INN])
  const fullNameObject = getFiscalFullName(
    state.F_FiscalType,
    state.countryRegistration
  )
  const shortNameObject = getFiscalShortName(
    state.F_FiscalType,
    state.countryRegistration
  )
  const certificateRegistrationNumber = getCertificateRegistrationNumber(
    state.F_FiscalType
  )
  const certificateRegistrationDate = getCertificateRegistrationDate(
    state.F_FiscalType
  )
  const certificateRegistrationImage = getCertificateRegistrationImage(
    state.F_FiscalType
  )
  const [shortName, setShortName] = useState(shortNameObject)
  const [fullName, setFullName] = useState(fullNameObject)

  useEffect(() => {
    setShortName(shortNameObject)
    setFullName(fullNameObject)
  }, [state.F_FiscalType])
  useEffect(() => {
    setValue('F_FiscalType', '7', { shouldDirty: true })
  }, [state.countryRegistration])

  return (
    <>
      <div>
        <div className="form-group">
          <div className="row">
            <div className="col-8 col-xs-12">
              {/* F_FullName */}
              <TextInput
                disabled={!state.agreed}
                lang={lang}
                content={fullName}
                tooltip={lang === 'eng' ? 'Enter the company name without LLC, Ltd., Inc., etc.' : 'Введите только название компании, без ООО, АО, ИП и т. д.'}
                onChange={F_FullName =>
                  setValue('F_FullName', F_FullName, { shouldDirty: true })
                }
                value={state.F_FullName || ''}
                valid={
                  state.F_FullName.length > 0
                    ? smartValidateString(state.F_FullName) &&
                      !errors.F_FullName
                    : !errors.F_FullName
                }
                required
                onBlur={() => setFocus('')}
                focus={focus === 'F_FullName' || undefined}
              />
            </div>
            <div className="col-4 col-xs-12">
              {/* F_ShortName */}
              <TextInput
                disabled={!state.agreed}
                lang={lang}
                content={shortName}
                tooltip={lang === 'eng' ? 'Enter the short company name without LLC, Ltd., Inc., etc.' : 'Введите краткое название, без ООО, АО, ИП и т. д.'}
                onChange={F_ShortName =>
                  setValue('F_ShortName', F_ShortName, { shouldDirty: true })
                }
                value={state.F_ShortName || ''}
                valid={
                  state.F_ShortName.length > 0
                    ? smartValidateString(state.F_ShortName) &&
                      !errors.F_ShortName
                    : !errors.F_ShortName
                }
                required
                onBlur={() => setFocus('')}
                focus={focus === 'F_ShortName' || undefined}
              />
            </div>
            <div className="col-4 col-xs-12">
              <TextInput
                disabled={!state.agreed}
                lang={lang}
                content={accountForm.F_INN}
                onChange={F_INN => {
                  setValue('F_INN', F_INN.replace(/\D/gim, '').substr(0, 14), { shouldDirty: true })
                }}
                value={state.F_INN || ''}
                required
                valid={
                  stringDigitsLength(state.F_INN, 14, false) &&
                  !errors.F_INN
                }
                mask={'99999999999999'}
              />
            </div>
            {/* certificateRegistrationNumber */}
            <div className="col-5 col-xs-12">
              <TextInput
                disabled={!state.agreed}
                lang={lang}
                content={certificateRegistrationNumber}
                onChange={certificateRegistrationNumber => {
                  setValue('certificateRegistrationNumber', certificateRegistrationNumber, { shouldDirty: true })
                }}
                required
                value={state.certificateRegistrationNumber || ''}
                valid={
                  validateString(
                    state.certificateRegistrationNumber,
                    30,
                    false
                  ) && !errors.certificateRegistrationNumber
                }
              />
            </div>
            <InnExist
              accountForm={accountForm}
              lang={lang}
              
              show={!state.isIdentityOk}
            />
            <div className="col-3 col-xs-12">
              <DatePicker
                lang={lang}
                accountForm={certificateRegistrationDate}
                accountFormKey="date"
                onChange={data => {
                  setValue('registeredDate', data, { shouldDirty: true })
                }}
                min={
                  new Date(
                    new Date().setFullYear(new Date().getFullYear() - 67)
                  )
                }
                max={new Date()}
                value={state.registeredDate}
                valid={!errors.registeredDate}
                required
              />
            </div>
            <div className="col-4 col-xs-12">
              <FileInput
                disabled={!state.agreed}
                lang={lang}
                url={UPLOADS_ENDPOINT}
                autorization={state.formId}
                target={state.documentsImages}
                content={accountForm.F_RegisteredDocumentScan}
                
                onChange={stateRegistrationCertificate => {
                  setValue('stateRegistrationCertificate', [...(getValues('stateRegistrationCertificate')||[]), stateRegistrationCertificate], { shouldDirty: true })
                }}
                valid={!errors.stateRegistrationCertificate}
                documentType="stateRegistrationCertificate"
                value={state.stateRegistrationCertificate[0]}
                imageErrors={state.imageError}
              />
            </div>
          </div>
        </div>
        <div className="form-group">
          <div className="row">
            <div className="col-12 col-xs-12">
              <h3 className={styles.header}>
                {accountForm.Fiscal_Current_Leader_Header[lang]}
              </h3>
            </div>
            <div className="col-4 col-xs-12">
              <TextInput
                disabled={!state.agreed}
                lang={lang}
                content={accountForm.headLastname}
                onChange={(headLastname: string) =>
                  setValue('headLastname', headLastname, { shouldDirty: true })
                }
                value={state.headLastname || ''}
                valid={
                  state.headLastname.length > 0
                    ? smartValidateString(state.headLastname) &&
                      !errors.headLastname
                    : !errors.headLastname
                }
              />
            </div>
            <div className="col-4 col-xs-12">
              <TextInput
                disabled={!state.agreed}
                lang={lang}
                content={accountForm.headFirstname}
                onChange={(headFirstname: string) =>
                  setValue('headFirstname', headFirstname, { shouldDirty: true })
                }
                value={state.headFirstname || ''}
                valid={
                  state.headFirstname.length > 0
                    ? smartValidateString(state.headFirstname) &&
                      !errors.headFirstname
                    : !errors.headFirstname
                }
              />
            </div>
            <div className="col-4 col-xs-12">
              <TextInput
                disabled={!state.agreed || state.noPatronymic}
                lang={lang}
                content={accountForm.headPatronymic}
                onChange={(headPatronymic: string) =>
                  setValue('headPatronymic', headPatronymic, { shouldDirty: true })
                }
                checkLabel={accountForm.noPatronymic[lang]}
                checkValue={state.noPatronymic}
                checkChange={() => {
                  setValue('noPatronymic', !state.noPatronymic, { shouldDirty: true })
                  setValue('headPatronymic', '', { shouldDirty: true })
                }}
                value={state.headPatronymic || ''}
                valid={
                  state.headPatronymic.length > 0
                    ? smartValidateString(state.headPatronymic) &&
                      !errors.headPatronymic
                    : !errors.headPatronymic
                }
              />
            </div>
            <div className="col-4 col-xs-12">
              <TextInput
                disabled={!state.agreed}
                lang={lang}
                content={accountForm.headLastnameGenitive}
                onChange={(headLastnameGenitive: string) =>
                  setValue('headLastnameGenitive', headLastnameGenitive, { shouldDirty: true })
                }
                value={state.headLastnameGenitive || ''}
                valid={
                  state.headLastnameGenitive.length > 0
                    ? smartValidateString(state.headLastnameGenitive) &&
                      !errors.headLastnameGenitive
                    : !errors.headLastnameGenitive
                }
              />
            </div>
            <div className="col-4 col-xs-12">
              <TextInput
                disabled={!state.agreed}
                lang={lang}
                content={accountForm.headFirstnameGenitive}
                onChange={(headFirstnameGenitive: string) =>
                  setValue('headFirstnameGenitive', headFirstnameGenitive, { shouldDirty: true })
                }
                value={state.headFirstnameGenitive || ''}
                valid={
                  state.headFirstnameGenitive.length > 0
                    ? smartValidateString(state.headFirstnameGenitive) &&
                      !errors.headFirstnameGenitive
                    : !errors.headFirstnameGenitive
                }
              />
            </div>
            <div className="col-4 col-xs-12">
              <TextInput
                disabled={!state.agreed || state.noPatronymic}
                lang={lang}
                content={accountForm.headPatronymicGenitive}
                onChange={(headPatronymicGenitive: string) =>
                  setValue('headPatronymicGenitive', headPatronymicGenitive, { shouldDirty: true })
                }
                checkLabel={accountForm.noPatronymic[lang]}
                checkValue={state.noPatronymic}
                checkChange={() => {
                  setValue('noPatronymic', !state.noPatronymic, { shouldDirty: true })
                  setValue('headPatronymicGenitive', '', { shouldDirty: true })
                }}
                value={state.headPatronymicGenitive || ''}
                valid={
                  state.headPatronymicGenitive.length > 0
                    ? smartValidateString(state.headPatronymicGenitive) &&
                      !errors.headPatronymicGenitive
                    : !errors.headPatronymicGenitive
                }
              />
            </div>
            <div className="col-12 col-xs-12">
              <p className={styles.warningMessage}>
                <img src={warningImage} alt="warningImage"></img>
                {accountForm.Fiscal_Warning_Genitive[lang]}
                <span>
                  {' '}
                  {accountForm.Fiscal_Warning_Genitive_Example[lang]}
                </span>
              </p>
            </div>
            <div className="col-4 col-xs-12">
              <SelectInput
                testName={'foundingDocument'}
                testErrorName={'error-foundingDocument'}
                lang={lang}
                noOptionsMessage={accountForm.foundingDocument.placeholder}
                content={accountForm.foundingDocument}
                onChange={data => {
                  setValue('foundingDocument', data, { shouldDirty: true })
                }}
                value={state.foundingDocument}
                options={foundingDocumentOptions}
                valid={
                  !errors.foundingDocument &&
                  validateString(state.foundingDocument, 100)
                }
                className={styles.inputl}
              />
            </div>
            <div className="col-4 col-xs-12">
              <TextInput
                disabled={!state.agreed}
                lang={lang}
                content={accountForm.headPosition}
                onChange={(headPosition: string) =>
                  setValue('headPosition', headPosition, { shouldDirty: true })
                }
                value={state.headPosition || ''}
                valid={
                  state.headPosition.length > 0
                    ? smartValidateString(state.headPosition) &&
                      !errors.headPosition
                    : !errors.headPosition
                }
              />
            </div>
            {lang !== 'eng' && (
              <div className="col-4 col-xs-12">
                <TextInput
                  disabled={!state.agreed}
                  lang={lang}
                  content={accountForm.headPositionGenitive}
                  onChange={(headPositionGenitive: string) =>
                    setValue('headPositionGenitive', headPositionGenitive, { shouldDirty: true })
                  }
                  value={state.headPositionGenitive || ''}
                  valid={
                    state.headPositionGenitive.length > 0
                      ? smartValidateString(state.headPositionGenitive) &&
                        !errors.headPositionGenitive
                      : !errors.headPositionGenitive
                  }
                />
              </div>
            )}
            <div className="col-4 col-xs-12">
              <FileInput
                disabled={!state.agreed}
                lang={lang}
                url={UPLOADS_ENDPOINT}
                autorization={state.formId}
                target={state.documentsImages}
                content={accountForm.F_Scan_Directors_Passport}
                
                onChange={data => {
                  setValue('F_Scan_Directors_Passport', [...(getValues('F_Scan_Directors_Passport')||[]), data], { shouldDirty: true })
                }}
                valid={!errors.F_Scan_Directors_Passport}
                documentType="Passport1"
                value={
                  state.F_Scan_Directors_Passport[
                    state.F_Scan_Directors_Passport.length - 1
                  ]
                }
                imageErrors={state.imageError}
              />
            </div>
            <div className="col-8 col-xs-12">
              <FileInput
                disabled={!state.agreed}
                lang={lang}
                url={UPLOADS_ENDPOINT}
                autorization={state.formId}
                target={state.documentsImages}
                content={accountForm.foundingDocument_Image}
                
                onChange={data => {
                  setValue('foundingDocument_Image', [...(getValues('foundingDocument_Image')||[]), data], { shouldDirty: true })
                }}
                valid={!errors.foundingDocument_Image}
                documentType="FoundingDocument"
                value={
                  state.foundingDocument_Image[
                    state.foundingDocument_Image.length - 1
                  ]
                }
                imageErrors={state.imageError}
              />
            </div>
            <div className="col-4 col-xs-12">
              <FileInput
                disabled={!state.agreed}
                lang={lang}
                url={UPLOADS_ENDPOINT}
                autorization={state.formId}
                target={state.documentsImages}
                content={accountForm.F_Scan_Directors_Passport_Address}
                
                onChange={data => {
                  setValue('F_Scan_Directors_Passport_Address', [...(getValues('F_Scan_Directors_Passport_Address')||[]), data], { shouldDirty: true })
                }}
                valid={
                  !errors.F_Scan_Directors_Passport_Address
                }
                documentType="Passport2"
                value={
                  state.F_Scan_Directors_Passport_Address[
                    state.F_Scan_Directors_Passport_Address.length - 1
                  ]
                }
                imageErrors={state.imageError}
              />
            </div>
            <div className="col-12 col-xs-12">
              <p className={styles.warningMessage}>
                <img src={warningImage} alt="warningImage"></img>
                {accountForm.Fiscal_Warning_Scan_Directors_Passport[lang]}
              </p>
            </div>
          </div>
        </div>
        <AddressInputs
          lang={lang}
          
          accountForm={accountForm}
          addressCountries={addressCountries}
          regionCityRegistered={regionCityRegistered}
          regionCityFactual={regionCityFactual}
        />
      </div>
    </>
  )
}

export default CompanyScreenOne
